import { useState } from "react";
import { Bell, Snowflake, Trophy, Calendar, CheckCircle, Crown, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const LEAGUES = [
  { id: "nhl", label: "NHL", icon: "🇺🇸" },
  { id: "shl", label: "SHL", icon: "🇸🇪" },
  { id: "khl", label: "KHL", icon: "🇷🇺" },
  { id: "liiga", label: "Liiga", icon: "🇫🇮" },
  { id: "del", label: "DEL", icon: "🇩🇪" },
  { id: "nl", label: "National League", icon: "🇨🇭" },
];

const TEAMS = [
  { id: "sweden-national", label: "Sweden National Team", icon: "🇸🇪" },
  { id: "finland-national", label: "Finland National Team", icon: "🇫🇮" },
  { id: "canada-national", label: "Canada National Team", icon: "🇨🇦" },
  { id: "usa-national", label: "USA National Team", icon: "🇺🇸" },
  { id: "russia-national", label: "Russia National Team", icon: "🇷🇺" },
  { id: "czechia-national", label: "Czechia National Team", icon: "🇨🇿" },
];

const PREMIUM_FEATURES = [
  { id: "ad-free", label: "Ad-Free Experience", icon: "🚫" },
  { id: "detailed-previews", label: "Detailed Game Previews", icon: "📊" },
  { id: "live-stats", label: "Live In-Game Stats", icon: "📈" },
  { id: "exclusive-content", label: "Exclusive Content", icon: "⭐" },
];

interface EventAlertModalProps {
  trigger?: React.ReactNode;
  defaultOlympicsAlert?: boolean;
}

const EventAlertModal = ({ trigger, defaultOlympicsAlert = false }: EventAlertModalProps) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [selectedLeagues, setSelectedLeagues] = useState<string[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [olympics2026Alerts, setOlympics2026Alerts] = useState(defaultOlympicsAlert);
  const [gameStartReminders, setGameStartReminders] = useState(false);
  const [premiumInterest, setPremiumInterest] = useState(false);
  const [selectedPremiumFeatures, setSelectedPremiumFeatures] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const toggleLeague = (leagueId: string) => {
    setSelectedLeagues((prev) =>
      prev.includes(leagueId)
        ? prev.filter((id) => id !== leagueId)
        : [...prev, leagueId]
    );
  };

  const toggleTeam = (teamId: string) => {
    setSelectedTeams((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId]
    );
  };

  const togglePremiumFeature = (featureId: string) => {
    setSelectedPremiumFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Use rate-limited edge function instead of direct insert
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/subscribe-alerts`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: email.trim(),
            favoriteLeagues: selectedLeagues,
            favoriteTeams: selectedTeams,
            olympics2026Alerts,
            gameStartReminders,
            premiumInterest,
            premiumFeaturesRequested: selectedPremiumFeatures,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Subscription failed");
      }

      setIsSuccess(true);
    } catch (error) {
      console.error("Subscription error:", error);
      toast({
        title: "Subscription failed",
        description: error instanceof Error ? error.message : "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset form after closing
    setTimeout(() => {
      setIsSuccess(false);
      setEmail("");
      setSelectedLeagues([]);
      setSelectedTeams([]);
      setOlympics2026Alerts(defaultOlympicsAlert);
      setGameStartReminders(false);
      setPremiumInterest(false);
      setSelectedPremiumFeatures([]);
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
            <Bell className="h-4 w-4" />
            Get Event Alerts
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-card border-border max-h-[80vh] overflow-y-auto top-[8%] translate-y-0 sm:top-[50%] sm:translate-y-[-50%]">
        {isSuccess ? (
          <div className="py-8 text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-primary" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-xl text-center">You're All Set!</DialogTitle>
              <DialogDescription className="text-center text-muted-foreground">
                You'll get reminders for {selectedTeams.includes("sweden-national") ? "Sweden games and " : ""}
                {olympics2026Alerts ? "Olympics 2026 " : ""}
                {selectedLeagues.length > 0 ? `${selectedLeagues.join(", ")} ` : ""}
                events and more!
              </DialogDescription>
            </DialogHeader>
            {premiumInterest && (
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-4 border border-primary/20">
                <div className="flex items-center justify-center gap-2 text-primary mb-2">
                  <Crown className="h-5 w-5" />
                  <span className="font-semibold">Premium Interest Noted!</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  We'll notify you when premium features like ad-free experience and detailed previews launch!
                </p>
              </div>
            )}
            <Badge variant="secondary" className="mt-4">
              Premium alerts coming soon
            </Badge>
            <Button onClick={handleClose} className="mt-4 w-full">
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 text-primary mb-2">
                <Snowflake className="h-5 w-5" />
                <span className="text-sm font-medium uppercase tracking-wider">
                  Free at Launch
                </span>
              </div>
              <DialogTitle className="text-xl flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Get Alerts for Big Ice Hockey Events
              </DialogTitle>
              <DialogDescription>
                Never miss a game! Get notifications for your favorite leagues, teams, and tournaments.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              {/* Email Field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-background border-border"
                  required
                />
              </div>

              {/* Favorite Leagues */}
              <div className="space-y-3">
                <Label className="text-foreground flex items-center gap-2">
                  <Trophy className="h-4 w-4 text-primary" />
                  Favorite Leagues
                </Label>
                <div className="flex flex-wrap gap-2">
                  {LEAGUES.map((league) => (
                    <button
                      key={league.id}
                      type="button"
                      onClick={() => toggleLeague(league.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                        selectedLeagues.includes(league.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-muted/50 text-muted-foreground border-border hover:border-primary/50"
                      }`}
                    >
                      {league.icon} {league.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Favorite Teams */}
              <div className="space-y-3">
                <Label className="text-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-primary" />
                  National Teams
                </Label>
                <div className="flex flex-wrap gap-2">
                  {TEAMS.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      onClick={() => toggleTeam(team.id)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all border ${
                        selectedTeams.includes(team.id)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-muted/50 text-muted-foreground border-border hover:border-primary/50"
                      }`}
                    >
                      {team.icon} {team.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Alert Options */}
              <div className="space-y-3 pt-2 border-t border-border">
                <Label className="text-foreground">Alert Preferences</Label>
                
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="olympics"
                    checked={olympics2026Alerts}
                    onCheckedChange={(checked) => setOlympics2026Alerts(!!checked)}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor="olympics"
                      className="text-sm font-medium cursor-pointer flex items-center gap-2"
                    >
                      🏅 Olympics 2026 Alerts
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Get notified about Milano Cortina ice hockey games
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="reminders"
                    checked={gameStartReminders}
                    onCheckedChange={(checked) => setGameStartReminders(!!checked)}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <label
                      htmlFor="reminders"
                      className="text-sm font-medium cursor-pointer flex items-center gap-2"
                    >
                      ⏰ Game Start Reminders
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Receive alerts 30 minutes before games
                    </p>
                  </div>
                </div>
              </div>

              {/* Premium Interest Section */}
              <div className="space-y-3 pt-3 border-t border-border">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="premium"
                    checked={premiumInterest}
                    onCheckedChange={(checked) => setPremiumInterest(!!checked)}
                    className="mt-0.5"
                  />
                  <div className="space-y-1 flex-1">
                    <label
                      htmlFor="premium"
                      className="text-sm font-medium cursor-pointer flex items-center gap-2"
                    >
                      <Crown className="h-4 w-4 text-amber-500" />
                      Interested in Premium Features
                      <Badge variant="outline" className="text-xs bg-gradient-to-r from-primary/10 to-accent/10">
                        Coming Soon
                      </Badge>
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Get early access to ad-free experience, detailed game previews & more
                    </p>
                  </div>
                </div>

                {premiumInterest && (
                  <div className="ml-6 space-y-2 animate-fade-in">
                    <Label className="text-sm text-muted-foreground flex items-center gap-2">
                      <Sparkles className="h-3 w-3" />
                      Which features interest you?
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {PREMIUM_FEATURES.map((feature) => (
                        <button
                          key={feature.id}
                          type="button"
                          onClick={() => togglePremiumFeature(feature.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                            selectedPremiumFeatures.includes(feature.id)
                              ? "bg-amber-500/20 text-amber-600 border-amber-500/50 dark:text-amber-400"
                              : "bg-muted/50 text-muted-foreground border-border hover:border-amber-500/50"
                          }`}
                        >
                          {feature.icon} {feature.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  "Subscribing..."
                ) : (
                  <>
                    <Bell className="h-4 w-4" />
                    Subscribe to Alerts
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By subscribing, you agree to receive event notifications. Unsubscribe anytime.
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EventAlertModal;
